import React, { useEffect, useState } from "react";
import { ChevronLeft, ChevronRight, ArrowUpRight } from "lucide-react";
import usePublicBanners from "@/lib/usePublicBanners";

export default function BannerCarousel() {
  const { banners, loaded } = usePublicBanners();
  const [idx, setIdx] = useState(0);

  useEffect(() => {
    if (banners.length <= 1) return;
    const t = setInterval(() => setIdx((i) => (i + 1) % banners.length), 6000);
    return () => clearInterval(t);
  }, [banners.length]);

  if (!loaded || banners.length === 0) return null;

  const cur = banners[idx];
  const go = (delta) => setIdx((i) => (i + delta + banners.length) % banners.length);
  const hasMeta = !!(cur.title || cur.link_url);

  return (
    <section className="relative pt-28 pb-2 md:pt-32" data-testid="banner-carousel">
      <div className="max-w-7xl mx-auto px-6 lg:px-10">
        {/* Image */}
        <div className="relative overflow-hidden border border-slate-200 bg-white group">
          <div className="relative w-full">
            <img
              key={cur.id}
              src={cur.image_base64}
              alt={cur.title || "Banner"}
              className="banner-slide block w-full h-auto"
              data-testid={`banner-image-${idx}`}
            />
          </div>

          {banners.length > 1 && (
            <>
              <button
                onClick={() => go(-1)}
                className="absolute left-3 top-1/2 -translate-y-1/2 h-10 w-10 bg-white/85 hover:bg-white text-slate-900 border border-slate-200 flex items-center justify-center transition-all opacity-0 group-hover:opacity-100"
                data-testid="banner-prev-btn"
                aria-label="Previous banner"
              >
                <ChevronLeft size={18} />
              </button>
              <button
                onClick={() => go(1)}
                className="absolute right-3 top-1/2 -translate-y-1/2 h-10 w-10 bg-white/85 hover:bg-white text-slate-900 border border-slate-200 flex items-center justify-center transition-all opacity-0 group-hover:opacity-100"
                data-testid="banner-next-btn"
                aria-label="Next banner"
              >
                <ChevronRight size={18} />
              </button>

              <div className="absolute bottom-4 left-1/2 -translate-x-1/2 flex items-center gap-1.5">
                {banners.map((_, i) => (
                  <button
                    key={i}
                    onClick={() => setIdx(i)}
                    className={`h-1.5 transition-all ${
                      i === idx ? "w-8 bg-[#0066FF]" : "w-2 bg-white/70 hover:bg-white"
                    }`}
                    data-testid={`banner-dot-${i}`}
                    aria-label={`Go to banner ${i + 1}`}
                  />
                ))}
              </div>
            </>
          )}
        </div>

        {/* Below-banner action bar */}
        {hasMeta && (
          <div
            className="mt-4 flex flex-col md:flex-row md:items-center md:justify-between gap-4 border border-slate-200 bg-white px-5 md:px-7 py-4"
            data-testid="banner-action-bar"
          >
            <div className="flex items-center gap-4 min-w-0">
              <span className="hidden md:inline-flex h-2 w-2 bg-[#0066FF] shrink-0" />
              {cur.title && (
                <h2 className="font-display text-base md:text-lg font-semibold text-slate-900 tracking-tight truncate">
                  {cur.title}
                </h2>
              )}
            </div>
            {cur.link_url && (
              <a
                href={cur.link_url}
                target={cur.link_url.startsWith("http") ? "_blank" : undefined}
                rel="noreferrer"
                className="btn-primary text-sm py-2.5 px-5 shrink-0 self-start md:self-auto"
                data-testid={`banner-cta-${idx}`}
              >
                Learn More <ArrowUpRight size={14} />
              </a>
            )}
          </div>
        )}
      </div>
    </section>
  );
}
